import { useQuery, useQueries } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { getTimeRangeMonths } from '../utils/dataHelpers';
import { AxiosError } from 'axios';
import { logger } from '../utils/logger';

// Query keys
export const MAIN_PERFORMANCE_QUERY_KEYS = {
  BRANDS: 'mainPerformanceBrands',
  BRANCHES: 'mainPerformanceBranches',
  DASHBOARD: 'mainPerformanceDashboard',
  PRODUCT_CATEGORIES: 'mainPerformanceProductCategories',
};

// API service functions
const apiService = {
  fetchBrands: async () => {
    const response = await apiClient.get('/brands');
    return response.data.map(brand => ({
      id: brand.BrandID,
      name: brand.BrandName,
      logo: brand.LogoAddress,
      currency: brand.Currency
    }));
  },

  fetchBranchesByBrand: async (brandId: number) => {
    const response = await apiClient.get(`/branches/brand/${brandId}`);
    return response.data.map(branch => ({
      id: branch.BranchID,
      name: branch.BranchName,
      brandId: branch.BrandId,
      currency: branch.Currency
    }));
  },

  fetchDashboardData: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/dashboard/${branchId}?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      // API returned success: false, likely no data
      return null;
    } else {
      throw new Error('Failed to fetch dashboard data');
    }
  },

  fetchProductCategoryData: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/dashboard/${branchId}/product-categories?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      // API returned success: false, likely no data
      return [];
    } else {
      throw new Error('Failed to fetch product category data');
    }
  },
};

// Hook to fetch brands
export function useBrands() {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_QUERY_KEYS.BRANDS],
    queryFn: apiService.fetchBrands,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch branches by brand
export function useBranches(brandId: number | null) {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_QUERY_KEYS.BRANCHES, brandId],
    queryFn: () => apiService.fetchBranchesByBrand(brandId!),
    enabled: !!brandId,
  });
}

// Hook to fetch main performance dashboard data
export function useMainPerformanceDashboard(branchId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_QUERY_KEYS.DASHBOARD, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      const dashboardData = await apiService.fetchDashboardData(branchId, timeRange);
      
      // If no data is returned, return empty defaults
      if (!dashboardData) {
        return {
          kpiData: {
            totalSales: 0,
            totalInvoices: 0,
            averageInvoiceValue: 0,
            customerSatisfaction: 0,
            membershipRatio: 0,
            newCustomers: 0,
            branchRank: 0,
            currency: null
          },
          salesTrend: [],
          salesTrendComparison: [],
          satisfactionData: [],
          satisfactionTrend: [],
          customerRetentionData: [],
          staffPerformanceData: [],
          comparisonData: {
            sales: { branch: 0, brand: 0, all: 0 },
            satisfaction: { branch: 0, brand: 0, all: 0 },
            newCustomers: { branch: 0, brand: 0, all: 0 },
            averageInvoice: { branch: 0, brand: 0, all: 0 },
            currency: null
          },
          satisfactionByShift: [],
          satisfactionByOrderType: [],
          productCategoryData: []
        };
      }
      
      // Process the data to ensure all fields have defaults
      return {
        kpiData: dashboardData.kpiData || {
          totalSales: 0,
          totalInvoices: 0,
          averageInvoiceValue: 0,
          customerSatisfaction: 0,
          membershipRatio: 0,
          newCustomers: 0,
          branchRank: 0,
          currency: null
        },
        salesTrend: dashboardData.salesTrend || [],
        salesTrendComparison: dashboardData.salesTrendComparison || [],
        satisfactionData: dashboardData.satisfactionData || [],
        satisfactionTrend: dashboardData.satisfactionTrend || [],
        customerRetentionData: dashboardData.customerRetentionData || [],
        staffPerformanceData: dashboardData.staffPerformanceData || [],
        comparisonData: dashboardData.comparisonData || {
          sales: { branch: 0, brand: 0, all: 0 },
          satisfaction: { branch: 0, brand: 0, all: 0 },
          newCustomers: { branch: 0, brand: 0, all: 0 },
          averageInvoice: { branch: 0, brand: 0, all: 0 },
          currency: null
        },
        satisfactionByShift: dashboardData.satisfactionByShift || [],
        satisfactionByOrderType: dashboardData.satisfactionByOrderType || [],
        productCategoryData: dashboardData.productCategoryData || []
      };
    },
    enabled: !!branchId,
    retry: 1,
  });
}

// Hook to fetch product category data
export function useProductCategories(branchId: number | null, timeRange: string = 'last6Months', enabled: boolean = true) {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_QUERY_KEYS.PRODUCT_CATEGORIES, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const productData = await apiService.fetchProductCategoryData(branchId, timeRange);
        
        if (productData && productData.length > 0) {
          return [...productData].sort((a, b) => (b.value || 0) - (a.value || 0));
        }
        
        return [];
      } catch (error) {
        logger.error('Failed to fetch product category data', 'MainPerformanceQueries', error);
        // Return empty array on error instead of throwing
        return [];
      }
    },
    enabled: !!branchId && enabled,
    retry: 1,
    retryDelay: 1000,
  });
}

// Combined hook that fetches all data for MainPerformanceDashboard
export function useMainPerformanceData(branchId: number | null, timeRange: string = 'last6Months') {
  const dashboardQuery = useMainPerformanceDashboard(branchId, timeRange);
  
  // Don't fetch product categories separately - they should come from main dashboard
  // This avoids the stuck loading issue
  
  return {
    isLoading: dashboardQuery.isLoading,
    isError: dashboardQuery.isError,
    error: dashboardQuery.error,
    data: dashboardQuery.data,
    refetch: () => {
      dashboardQuery.refetch();
    }
  };
}